import type { ReactNode } from 'react';
import { Badge } from '@clay-run/glaze/badge';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { TextLink } from '@clay-run/glaze/textlink';

import { SfdcCloudIcon } from '../../../_shared/components/flow';
import type { CampaignMember } from '../../../_shared/launcher-campaign.types';
import { memberDisplay } from './MemberRowCells';

/**
 * A member's Contact / Account / Flags sections — extracted verbatim from
 * `MemberDetailDrawer` (plan 2026-10-15-003 U5) so the Review step's
 * one-person test panel can show the same details under the test, while
 * Qualify and Sequences keep opening the plain details drawer. Presentation
 * only: every field already lives on `CampaignMember`.
 */

/** One label/value pair. Written once here — every section below reuses it. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt>
        <Text size="xs" variant="tertiary">
          {label}
        </Text>
      </dt>
      <dd>
        <Text size="sm">{value ?? '—'}</Text>
      </dd>
    </div>
  );
}

/** A name value, rendered as a `mailto:` `TextLink` when an email exists — the Launcher's own behavior for these three owner fields, ported (there is no owner page to link to instead). */
function OwnerField({ label, name, email }: { label: string; name: string | null; email: string | null }) {
  const value = email ? (
    <TextLink href={`mailto:${email}`} size="sm">
      {name ?? email}
    </TextLink>
  ) : (
    name
  );
  return <Field label={label} value={value} />;
}

/** The small uppercase section heading — one definition for every Playbooks member surface. */
export function SectionTitle({ children }: { children: string }) {
  return (
    <Text size="xs" weight="medium" variant="tertiary" className="uppercase tracking-wider">
      {children}
    </Text>
  );
}

export function MemberDetailFields({ member }: { member: CampaignMember }) {
  const { email } = memberDisplay(member);
  const hasFlags = Boolean(member.suppressed || member.no_longer_at_company || member.suppression_reasons);

  return (
    <>
      <section className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <SectionTitle>Contact</SectionTitle>
          <SfdcCloudIcon kind="Contact" id={member.contact_id} />
          {!member.contact_id && <SfdcCloudIcon kind="Lead" id={member.lead_id} />}
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
          <Field label="Name" value={member.contact_name} />
          <Field label="Title" value={member.contact_title} />
          <Field
            label="Email"
            value={
              email ? (
                <TextLink href={`mailto:${email}`} size="sm">
                  {email}
                </TextLink>
              ) : null
            }
          />
          <Field
            label="CM Status"
            value={member.cm_status ? <StatusBadge status="neutral">{member.cm_status}</StatusBadge> : null}
          />
          <Field label="Fit Score" value={member.fit_score != null ? member.fit_score.toString() : null} />
        </dl>
      </section>

      <section className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <SectionTitle>Account</SectionTitle>
          <SfdcCloudIcon kind="Account" id={member.contact_account_id} />
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
          <Field label="Name" value={member.account_name} />
          <Field label="Sales Tier" value={member.sales_tier ? <Badge>{member.sales_tier}</Badge> : null} />
          <OwnerField label="GTME Owner" name={member.gtme_owner_name} email={member.gtme_owner_email} />
          <OwnerField label="Growth Strategist" name={member.gs_owner_name} email={member.gs_owner_email} />
          <OwnerField label="ClayDR" name={member.claydr_name} email={member.claydr_email} />
        </dl>
      </section>

      {hasFlags && (
        <section className="flex flex-col gap-2">
          <SectionTitle>Flags</SectionTitle>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
            {member.suppressed != null && <Field label="Suppressed" value={member.suppressed ? 'Yes' : 'No'} />}
            {member.suppression_reasons && <Field label="Suppression Reasons" value={member.suppression_reasons} />}
            {member.no_longer_at_company != null && (
              <Field label="Left Company" value={member.no_longer_at_company ? 'Yes' : 'No'} />
            )}
          </dl>
        </section>
      )}
    </>
  );
}
